import { action, observable, runInAction } from 'mobx';
import { Container, Inject } from 'typescript-ioc';
import { AbstractFormViewModelController } from '../../../../../../../../../infrastructure/implementations/form-viewmodel-controller';
import { InitialData } from '../../../../../../../../../infrastructure/form-viewmodel-controller';
import { IErrorParsing } from '../../../../../../../../../infrastructure/error-parsing';
import { alertServerError } from '../../../../../../../../global/modal/alerts-host/alert-templates';
import { errorText } from '../../../../../../../../../constants/common-labels.constants';
import { IAccountOrganizationDTO, IAccountOrganizationsDAO } from '../../../../../../../../../api/account-organizations.dao';
import { AccountOrganizationsDAO } from '../../../../../../../../../api/implementations/account-organizations.dao';
import { IAccountContactDTO, IAccountContactsDAO, ICreateAccountContactDTO } from '../../../../../../../../../api/account-contacts.dao';
import { AccountContactsDAO } from '../../../../../../../../../api/implementations/account-contacts.dao';
import { ACCOUNT_ORGANIZATION_ID, BIOGRAPHY, FIRST_NAME, LAST_NAME, SPEAKER_FORM_CONFIG, TITLE } from './speaker-form.config';
import { transpileClassesToInlineStyles, transpileInlineStylesToClasses } from '../../../../../../../../global/rich-text-editor/utils/transpiler';
import { AccountLevelUploadImageS3VM } from '../../../../../../../../_events/_manage-event/shared/upload-image/upload-image-to-s3-account-level.viewmodel';

export class SpeakerFormVM extends AbstractFormViewModelController {
    public static createEmpty(accountId: number, onCancel: () => void, onConfirm: (contact: IAccountContactDTO) => void): SpeakerFormVM {
        return new SpeakerFormVM({}, accountId, onCancel, onConfirm);
    }

    public static createFromSpeakerDTO(
        speaker: IAccountContactDTO,
        accountId: number,
        onCancel: () => void,
        onConfirm: (contact: IAccountContactDTO) => void,
    ): SpeakerFormVM {
        const initial: InitialData = {
            [FIRST_NAME]: speaker.firstName,
            [LAST_NAME]: speaker.lastName,
            [TITLE]: speaker.title,
            [ACCOUNT_ORGANIZATION_ID]: speaker.accountOrganizationId,
            [BIOGRAPHY]: transpileInlineStylesToClasses(speaker.biography),
        };
        return new SpeakerFormVM(initial, accountId, onCancel, onConfirm, speaker);
    }

    @observable
    private organizations: IAccountOrganizationDTO[] = [];
    @observable
    public isAddOrganizationModalOpen: boolean = false;
    @observable
    public isSaving: boolean = false;

    private readonly uploadProfileImageS3VM: AccountLevelUploadImageS3VM;

    private constructor(
        initial: InitialData,
        private readonly accountId: number,
        private readonly onCancelCallback: () => void,
        private readonly onConfirmCallback: (contact: IAccountContactDTO) => void,
        private readonly initialSpeaker?: IAccountContactDTO,
        @Inject private readonly accountOrganizationsDAO: IAccountOrganizationsDAO = Container.get(AccountOrganizationsDAO),
        @Inject private readonly accountContactsDAO: IAccountContactsDAO = Container.get(AccountContactsDAO),
        @Inject private readonly errorParsingService: IErrorParsing = Container.get(IErrorParsing),
    ) {
        super(initial, 'SpeakerFormVM', SPEAKER_FORM_CONFIG);
        this.uploadProfileImageS3VM = new AccountLevelUploadImageS3VM(this.accountId, 'profileImage', 'account-contact');
        this.uploadProfileImageS3VM.addImage(
            initialSpeaker?.profileImageWithUrl?.assetUrl,
            initialSpeaker?.profileImage?.title ?? initialSpeaker?.profileImageWithUrl?.title,
            initialSpeaker?.profileImage?.objectKey ?? initialSpeaker?.profileImageWithUrl?.objectKey
        );
    }

    public isValid = (): boolean => {
        return this.isVmValid();
    };

    public isEditMode = (): boolean => {
        return this.initialSpeaker != null;
    };

    public isImageUploading = (): boolean => {
        return this.uploadProfileImageS3VM.isImageUploading;
    };

    public getAccountId = (): number => {
        return this.accountId;
    };

    public getUploadProfileImageS3VM = (): AccountLevelUploadImageS3VM => {
        return this.uploadProfileImageS3VM;
    };

    public getOrganizations = (): IAccountOrganizationDTO[] => {
        return this.organizations;
    };

    public getOrganizationNames = (): string[] => {
        return this.organizations.map(organization => organization.name);
    };

    public cancel = (): void => {
        this.onCancelCallback();
    };

    public toCreateContactDTO = (): ICreateAccountContactDTO => {
        const profileImage = this.uploadProfileImageS3VM.getImageToApi();
        return {
            firstName: this.getTrimmedStringValue(FIRST_NAME),
            lastName: this.getTrimmedStringValue(LAST_NAME),
            title: this.getTrimmedStringValue(TITLE),
            accountOrganizationId: this.getValue(ACCOUNT_ORGANIZATION_ID) as number,
            biography: transpileClassesToInlineStyles(this.getTrimmedStringValue(BIOGRAPHY)).trim(),
            ...profileImage
        };
    };

    public submit = async (): Promise<void> => {
        this.setVmTouched();
        if (!this.isValid() || this.isSaving || this.isImageUploading()) return;
        if (this.isEditMode()) {
            await this.updateSpeaker();
        } else {
            await this.createContact();
        }
    };

    public async loadOrganizations(): Promise<void> {
        try {
            const organizations = await this.accountOrganizationsDAO.getAccountOrganizations(this.accountId);
            runInAction(() => {
                this.organizations = organizations.slice().sort((a, b) => a.name.localeCompare(b.name));
            });
        } catch (error) {
            const message = this.errorParsingService.extractMessage(error);
            alertServerError(errorText(), message);
        }
    }

    @action
    public updateOrganizationsList = (organizations: IAccountOrganizationDTO[]): void => {
        this.organizations = organizations;
    };

    public selectOrganization = (organizationId: number): void => {
        this.setValue(ACCOUNT_ORGANIZATION_ID, organizationId);
    };

    @action
    public openAddOrganizationModal = (): void => {
        this.isAddOrganizationModalOpen = true;
    };

    @action
    public closeAddOrganizationModal = (): void => {
        this.isAddOrganizationModalOpen = false;
    };

    @action
    public applyNewOrganization = (organization: IAccountOrganizationDTO): void => {
        const alreadyExists = this.organizations.some(item => item.accountOrganizationId === organization.accountOrganizationId);
        if (!alreadyExists) {
            const sorted = [...this.organizations, organization].sort((a, b) => a.name.localeCompare(b.name));
            this.updateOrganizationsList(sorted);
        }
        this.selectOrganization(organization.accountOrganizationId);
        this.closeAddOrganizationModal();
    };

    @action
    private async createContact(): Promise<void> {
        this.isSaving = true;
        try {
            const contact = await this.accountContactsDAO.createAccountContact(this.accountId, this.toCreateContactDTO());
            this.onConfirmCallback(contact);
        } catch (error) {
            const message = this.errorParsingService.extractMessage(error);
            alertServerError(errorText(), message);
        } finally {
            runInAction(() => {
                this.isSaving = false;
            });
        }
    }

    @action
    private async updateSpeaker(): Promise<void> {
        this.isSaving = true;
        try {
            const contact = await this.accountContactsDAO.updateAccountContact(this.accountId, this.toUpdateContactDTO());
            this.onConfirmCallback(contact);
        } catch (error) {
            const message = this.errorParsingService.extractMessage(error);
            alertServerError(errorText(), message);
        } finally {
            runInAction(() => {
                this.isSaving = false;
            });
        }
    }

    private toUpdateContactDTO = (): IAccountContactDTO => {
        return {
            accountContactId: this.initialSpeaker.accountContactId,
            ...this.toCreateContactDTO(),
        };
    };

    private getTrimmedStringValue = (key: string): string => {
        return ((this.getValue(key) || '') as string).trim();
    };
}